import React, { useEffect } from 'react';
import { useContest } from '../../Context/ContestContext';
import { useGames } from '../../Context/GameContext';
import { getMediaUrl } from '../../Services/apiService';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Play, Copy, ArrowLeft, X } from 'lucide-react';
import { Button, Card, Container, Badge, Row, Col } from 'react-bootstrap';
import CountdownOverlay from '../../Components/CountdownOverlay';
import Swal from 'sweetalert2';

const Lobby = () => {
  const { gameState, roomCode, players, createRoom, startGame, resetContest, terminateGame, kickUser, countdown } = useContest();
  const { getActiveGame, activeGameId } = useGames();
  const navigate = useNavigate();
  
  const activeGame = getActiveGame();

  useEffect(() => {
    if (gameState === 'PLAYING' || gameState === 'COUNTDOWN') {
      navigate('/contest');
    }
  }, [gameState, navigate]);

  // Eğer Lobby'e girildiğinde eski oyun bitmişse, tamamen temizle
  useEffect(() => {
     if (gameState === 'FINISHED' && roomCode) {
        resetContest();
     }
  }, [gameState, roomCode, resetContest]);

  const handleExit = (path) => {
    if (roomCode) {
        Swal.fire({
            title: 'Yarışmayı sonlandırmak istediğinize emin misiniz?',
            text: "Bu sayfadan ayrılırsanız mevcut yarışma sonlandırılacak ve tüm oyuncular atılacaktır!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Evet, Sonlandır',
            cancelButtonText: 'Vazgeç'
        }).then((result) => {
            if (result.isConfirmed) {
                terminateGame();
                navigate(path);
            }
        });
    } else {
        resetContest();
        navigate(path);
    }
  };

  if (!activeGameId) {
     return (
        <Container className="d-flex flex-column align-items-center justify-content-center text-center p-5">
           <h3 className="mb-4">Herhangi bir oyun seçilmedi.</h3>
           <Button variant="primary" onClick={() => navigate('/games')}>
              Oyun Listesine Git
           </Button>
        </Container>
     );
  }

  if (!roomCode) {
    return (
      <Container className="d-flex align-items-center justify-content-center" style={{ minHeight: '80vh' }}>
        <motion.div
           initial={{ scale: 0.9, opacity: 0 }}
           animate={{ scale: 1, opacity: 1 }}
           className="text-center w-100"
           style={{ maxWidth: '600px' }}
        >
           <Card className="custom-card border-0 p-5">
              <div className="mb-4 d-flex flex-column align-items-center">
                 {activeGame?.image ? (
                    <div className="rounded-4 overflow-hidden shadow-sm mb-3 border border-light" style={{ width: 180, height: 120 }}>
                        <img 
                           src={getMediaUrl(activeGame.image)} 
                           alt="Game Cover" 
                           className="w-100 h-100 object-fit-cover" 
                        />
                    </div>
                 ) : (
                    <div className="rounded-4 bg-light d-flex align-items-center justify-content-center mb-3 border" style={{ width: 180, height: 120 }}>
                        <span className="text-muted small">Kapak Resmi Yok</span>
                    </div>
                 )}
                 <h2 className="fw-bold text-gradient mb-1">{activeGame?.title}</h2>
                 <Badge bg="primary" className="opacity-75">{activeGame?.questions?.length} Soru</Badge>
              </div>

              <h1 className="display-6 fw-bold mb-4">Hazır mısınız?</h1>
              
              <div className="d-grid gap-3">
                 <Button 
                   variant="primary" 
                   size="lg" 
                   onClick={() => createRoom(activeGame)}
                   className="py-3 fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2 border-0"
                 >
                   <Play fill="currentColor" />
                   Odayı Oluştur ve Başlat
                 </Button>
                 
                 <Button 
                   variant="light" 
                   onClick={() => handleExit('/games')}
                   className="text-muted border-0"
                 >
                   Farklı Oyun Seç
                 </Button>
              </div>
           </Card>
        </motion.div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-2 h-100 d-flex flex-column">
      <Row className="mb-4 align-items-end">
        <Col md={8}>
           <div className="d-flex align-items-center gap-3 mb-2">
              <Button variant="light" size="sm" onClick={() => handleExit('/games')} className="rounded-circle p-2" title="Çıkış"><ArrowLeft size={16}/></Button>
              <h6 className="text-muted fw-bold text-uppercase mb-0" style={{ letterSpacing: '2px' }}>ODA KODU</h6>
           </div>
           <div className="d-flex align-items-center gap-3">
              <h1 className="display-1 fw-bold text-dark mb-0 font-monospace" style={{ letterSpacing: '5px' }}>{roomCode}</h1>
              <Button variant="light" className="rounded-circle p-3 shadow-none border">
                 <Copy size={24} className="text-secondary" />
              </Button>
           </div>
           <p className="mt-2 text-primary fw-bold mb-0">Seçili Oyun: {activeGame?.title}</p>
           <p className="text-secondary fw-semibold small mb-0 mt-1">
              Oluşturan: {activeGame?.Host?.username || activeGame?.creatorName || 'Universal Team'}
           </p>
        </Col>
        <Col md={4} className="text-md-end">
           <h6 className="text-muted fw-bold text-uppercase mb-2" style={{ letterSpacing: '2px' }}>DURUM</h6>
           <Badge bg="success" className="px-3 py-2 fw-medium d-inline-flex align-items-center gap-2">
              <span className="spinner-grow spinner-grow-sm" role="status" aria-hidden="true" />
              Oyuncular bekleniyor...
           </Badge>
        </Col>
      </Row>

      <div className="flex-fill">
        <h4 className="fw-bold text-dark mb-4 d-flex align-items-center gap-2">
           <Users className="text-primary" />
           Katılan Oyuncular <Badge bg="light" text="dark" pill className="border">{players.length}</Badge>
        </h4>

        <Row className="g-3">
          {players.map((player) => (
             <Col xs={6} md={3} lg={2} key={player.id}>
               <motion.div
                 initial={{ scale: 0, opacity: 0 }}
                 animate={{ scale: 1, opacity: 1 }}
               >
                 <Card className="custom-card border-0 text-center py-3 position-relative">
                    <Button 
                        variant="danger" 
                        size="sm" 
                        className="position-absolute top-0 end-0 m-1 rounded-circle p-1"
                        style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.8 }}
                        onClick={() => kickUser(player.id)}
                        title="Oyuncuyu At"
                    >
                        <X size={14} />
                    </Button>
                    <div 
                      className="mx-auto mb-2 rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold"
                      style={{ width: 48, height: 48, fontSize: '1.2rem'}}
                    >
                       {player.nickname ? player.nickname.charAt(0) : '?'}
                    </div>
                    <div className="fw-bold text-dark text-truncate px-2">{player.nickname || player.name}</div>
                 </Card>
               </motion.div>
             </Col>
          ))}
        </Row>
      </div>

      <div className="mt-5 pt-4 border-top d-flex justify-content-end">
         <Button 
            variant="primary" 
            size="lg" 
            className="px-5 py-3 fw-bold shadow-sm"
            onClick={startGame}
            // disabled={players.length === 0}
         >
            Yarışmayı Başlat
         </Button>
      </div>

      <CountdownOverlay countdown={countdown} />
    </Container>
  );
};

export default Lobby;
